import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { CmspChatStrings } from './contract';

export interface LiveMessageIdentity {
  messageId?: string | null;
  senderKind?: string | null;
  senderName?: string | null;
  liveCategory?: string | null;
  recoveryState?: LiveRecoveryState | null;
}

export type LivePresentationPhase = 'connecting' | 'waiting' | 'connected' | 'failed';
export interface LivePresentationState {
  phase: LivePresentationPhase;
  humanPresent: boolean;
  agentName: string | null;
}

export const initialLivePresentation: LivePresentationState = {
  phase: 'connecting', humanPresent: false, agentName: null,
};

export const legacyLivePresentation: LivePresentationState = {
  phase: 'connected', humanPresent: true, agentName: null,
};

/** Structured presentation only: provider identity or assignment timestamps never imply a human. */
export function reduceLivePresentation(
  state: LivePresentationState,
  frame: LiveMessageIdentity,
): LivePresentationState {
  const category = frame.liveCategory;
  const name = frame.senderName?.trim() || null;
  if (frame.recoveryState === 'failed' || frame.recoveryState === 'auth-failed' || category === 'error') {
    return { ...state, phase: 'failed', humanPresent: false };
  }
  if (category === 'ended') {
    return { ...state, humanPresent: false };
  }
  if (category === 'agent-left' || category === 'transferred') {
    return { phase: 'waiting', humanPresent: false, agentName: null };
  }
  if (category === 'agent-joined' || frame.senderKind === 'human' || frame.senderKind === 'agent') {
    return { phase: 'connected', humanPresent: true, agentName: name ?? state.agentName };
  }
  if (category === 'connecting' && state.humanPresent && frame.recoveryState !== 'recovering') return state;
  if (frame.recoveryState === 'recovering' || category === 'connecting') {
    return { ...state, phase: 'connecting' };
  }
  if (frame.recoveryState === 'active') {
    return { ...state, phase: state.humanPresent ? 'connected' : 'waiting' };
  }
  if (category === 'waiting' || category === 'queue-position') {
    return state.humanPresent ? state : { ...state, phase: 'waiting' };
  }
  return state;
}

/** IDs are opaque: only an absent/blank ID opts out of replay protection. */
export function sourceMessageId(value?: string | null): string | undefined {
  return value?.trim() ? value : undefined;
}

/** Apply a presentation-only edit; never append an unknown ID or change message identity. */
export function replaceLiveMessage<T extends { messageId?: string | null }>(
  messages: T[], messageId: string | null | undefined, patch: Partial<T>,
): T[] {
  const id = sourceMessageId(messageId);
  if (!id || !messages.some(message => message.messageId === id)) return messages;
  return messages.map(message => message.messageId === id
    ? { ...message, ...patch, messageId: message.messageId } : message);
}

interface HistoryIdentity {
  role: string;
  messageId?: string | null;
  senderName?: string | null;
  liveCategory?: string | null;
  isLiveAgent?: boolean;
}

/** Replay oldest-first lifecycle state without interpreting AI text or legacy notices. */
export function restoreLiveHistory<T extends HistoryIdentity>(gate: LiveMessageGate, history: T[]) {
  let agentName: string | null = null;
  const messages = history.filter(message => {
    const human = message.role === 'agent' && message.isLiveAgent === true;
    const lifecycle = message.role === 'system' && !!message.liveCategory;
    if (!human && !lifecycle) return gate.remember(message.messageId);
    const accepted = gate.accept({
      messageId: message.messageId,
      senderName: message.senderName,
      senderKind: human ? 'human' : 'system',
      liveCategory: lifecycle ? message.liveCategory : undefined,
    });
    if (accepted?.agentName !== undefined) agentName = accepted.agentName;
    return accepted?.show === true;
  });
  return { messages, agentName };
}

/**
 * One synchronous gate shared by all streams in a conversation. Keep it in a ref,
 * outside React state updaters: two callbacks can arrive before React commits.
 */
export class LiveMessageGate {
  private readonly seen = new Set<string>();
  private joined: { sender?: string; hasId: boolean } | null = null;

  reset(): void {
    this.seen.clear();
    this.joined = null;
  }

  remember(messageId?: string | null): boolean {
    const id = sourceMessageId(messageId);
    if (!id) return true;
    if (this.seen.has(id)) return false;
    this.seen.add(id);
    return true;
  }

  accept(frame: LiveMessageIdentity): { show: boolean; agentName?: string | null } | null {
    if (frame.senderKind === 'customer' || frame.senderKind === 'user') return null;
    if (frame.senderKind === 'bot') return this.remember(frame.messageId) ? { show: true } : null;
    const category = frame.liveCategory;
    if (!this.remember(frame.messageId)) {
      if (category === 'ended') this.joined = null;
      return null;
    }
    const sender = frame.senderName?.trim() || undefined;
    if (category === 'agent-left' || category === 'ended' || category === 'transferred') {
      this.joined = null;
      return { show: true, agentName: category === 'transferred' ? sender ?? null : null };
    }
    if (category === 'agent-joined') {
      const hasId = sourceMessageId(frame.messageId) !== undefined;
      // Collapse only redundant connection STATE involving an unkeyed synthetic
      // notice, never two different message-backed joins or a named agent change.
      const redundant = this.joined !== null
        && (!hasId || !this.joined.hasId)
        && (!sender || !this.joined.sender || sender === this.joined.sender);
      this.joined = {
        sender: sender ?? this.joined?.sender,
        hasId: hasId || (redundant && this.joined?.hasId === true),
      };
      return { show: !redundant, agentName: sender };
    }
    return { show: true, agentName: frame.senderKind === 'human' ? sender : undefined };
  }
}

/**
 * Presentation acknowledgement, never delivery acknowledgement. Only committed
 * human rows intersecting the visible viewport are eligible; hidden chrome and
 * background tabs retain their unread rows until they become visible.
 */
export function useVisibleLiveRead(
  root: RefObject<HTMLElement | null>,
  messages: readonly unknown[],
  scope: unknown,
  enabled: boolean,
  markRead: ((messageId: string) => Promise<void>) | undefined,
): void {
  const current = useRef({ scope, enabled, markRead });
  current.current = { scope, enabled, markRead };
  const acknowledged = useRef<{ scope: unknown; ids: Set<string> }>({ scope, ids: new Set() });
  if (acknowledged.current.scope !== scope) acknowledged.current = { scope, ids: new Set() };

  useEffect(() => {
    const container = root.current;
    if (!container || !enabled || !markRead) return;
    let disposed = false;
    const seen = acknowledged.current.ids;
    const intersecting = new Set<Element>();
    const rows = Array.from(container.querySelectorAll<HTMLElement>('[data-live-read-id]'));
    const visible = (row: HTMLElement) => {
      if (!row.isConnected || !container.contains(row)) return false;
      for (let element: HTMLElement | null = row; element; element = element.parentElement) {
        const style = getComputedStyle(element);
        if (element.hidden || element.getAttribute('aria-hidden') === 'true'
          || style.display === 'none' || style.visibility === 'hidden') return false;
      }
      const box = row.getBoundingClientRect();
      const bounds = container.getBoundingClientRect();
      return box.width > 0 && box.height > 0
        && box.bottom > Math.max(0, bounds.top) && box.top < Math.min(innerHeight, bounds.bottom)
        && box.right > Math.max(0, bounds.left) && box.left < Math.min(innerWidth, bounds.right);
    };
    const flush = () => {
      if (disposed || current.current.scope !== scope || !current.current.enabled
        || document.visibilityState !== 'visible') return;
      const candidates = rows.filter(row => !seen.has(row.dataset.liveReadId!)
        && (observer ? intersecting.has(row) : true) && visible(row));
      const latest = candidates.at(-1)?.dataset.liveReadId;
      if (!latest) return;
      const ids = candidates.map(row => row.dataset.liveReadId!);
      ids.forEach(id => seen.add(id));
      void Promise.resolve().then(() => {
        if (disposed || current.current.scope !== scope || !current.current.enabled
          || document.visibilityState !== 'visible' || !visible(candidates.at(-1)!)) {
          ids.forEach(id => seen.delete(id));
          return;
        }
        return current.current.markRead?.(latest);
      }).catch(() => {
        // Retry only on another presentation event, never in a request/error loop.
        ids.forEach(id => seen.delete(id));
      });
    };
    const observer = typeof IntersectionObserver === 'undefined' ? undefined : new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) intersecting.add(entry.target);
        else intersecting.delete(entry.target);
      }
      flush();
    });
    rows.forEach(row => observer?.observe(row));
    document.addEventListener('visibilitychange', flush);
    container.addEventListener('scroll', flush);
    window.addEventListener('resize', flush);
    if (!observer) flush();
    return () => {
      disposed = true;
      observer?.disconnect();
      document.removeEventListener('visibilitychange', flush);
      container.removeEventListener('scroll', flush);
      window.removeEventListener('resize', flush);
    };
  }, [root, messages, scope, enabled, markRead]);
}

export type LiveRecoveryState = 'recovering' | 'active' | 'auth-failed' | 'failed';

export function isTerminalLiveFailure(error: unknown): boolean {
  const state = (error as { recoveryState?: string } | null)?.recoveryState;
  return state === 'failed' || state === 'auth-failed';
}

export function isUncertainLiveSend(error: unknown): boolean {
  const status = error as { code?: string; error?: string } | null;
  return ['send_status_unknown', 'send_confirmation_pending'].includes(status?.code ?? status?.error ?? '');
}

export function liveMessageScanFeedback(error: unknown, strings: CmspChatStrings): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const code = 'code' in error && typeof error.code === 'string'
    ? error.code : 'error' in error ? error.error : undefined;
  if (code === 'live_chat_message_blocked') return strings.status.liveMessageBlocked;
  if (code === 'live_chat_scan_unavailable') return strings.status.liveMessageScanUnavailable;
  if (code === 'live_chat_message_too_long') return strings.status.liveMessageTooLong;
  return undefined;
}

export interface LiveSendIdentity {
  clientOperationId?: string;
  messageId?: string | null;
  pendingSend?: boolean;
  deliveryUncertain?: boolean;
  readSequence?: number | null;
  readOn?: string | null;
}

export function uncertainLiveSendIdentity(error: unknown, current?: LiveSendIdentity): Pick<LiveSendIdentity, 'clientOperationId' | 'deliveryUncertain' | 'pendingSend'> {
  const operation = (error as { clientOperationId?: unknown } | null)?.clientOperationId;
  if (sourceMessageId(current?.messageId) && current?.clientOperationId
    && (!operation || operation === current.clientOperationId)) return {};
  return {
    pendingSend: false,
    deliveryUncertain: true,
    ...(typeof operation === 'string' && operation.trim() ? { clientOperationId: operation } : {}),
  };
}

/** Pending drafts keep their operation identity but are not transcript rows until accepted. */
export function revealLiveSend<T extends LiveSendIdentity>(messages: T[], clientOperationId: string): T[] {
  return messages.map(message => message.clientOperationId === clientOperationId
    ? { ...message, pendingSend: false } : message);
}

/** Only a transport-attested operation/provider pair can resolve an uncertain send. */
export function reconcileLiveSend<T extends LiveSendIdentity>(messages: T[], acknowledgement: LiveSendIdentity): T[] {
  const { clientOperationId } = acknowledgement;
  const messageId = sourceMessageId(acknowledgement.messageId);
  if (!clientOperationId || !messageId) return messages;
  return messages.map(message => message.clientOperationId === clientOperationId
    && (!message.messageId || message.messageId === messageId)
    ? {
      ...message, messageId, pendingSend: false, deliveryUncertain: false,
      readSequence: acknowledgement.readSequence ?? message.readSequence,
      readOn: acknowledgement.readOn ?? message.readOn,
    } : message);
}

export interface LiveReceiptIdentity {
  messageId?: string | null;
  readSequence?: number | null;
  readOn?: string | null;
}

export function confirmedReadIdentity(message: LiveReceiptIdentity): {
  messageId?: string; readSequence?: number; readOn?: string;
} {
  const messageId = sourceMessageId(message.messageId);
  if (!messageId || !Number.isSafeInteger(message.readSequence) || (message.readSequence ?? 0) <= 0) return {};
  return { messageId, readSequence: message.readSequence!, readOn: validReadIdentity(message) ? message.readOn! : undefined };
}

/** Correlate attested support receipts by opaque provider identity, never UI send order. */
export function useLiveReadReceipts(scope: unknown) {
  const current = useRef(scope);
  current.current = scope;
  const [state, setState] = useState<{
    scope: unknown;
    enabled: boolean;
    receipts: Map<string, { sequence: number; at: string }>;
  }>({ scope, enabled: false, receipts: new Map() });
  const observe = useCallback((frame: LiveReceiptIdentity & {
    liveCategory?: string | null;
    liveCapabilities?: { incomingReadReceipts: boolean; sendReadReceipts: boolean };
  }, observedScope: unknown) => {
    if (observedScope !== current.current) return;
    if (!frame.liveCapabilities && frame.liveCategory !== 'read-receipt') return;
    setState(previous => {
      if (current.current !== observedScope) return previous;
      const next = previous.scope === observedScope ? previous : { scope: observedScope, enabled: false, receipts: new Map() };
      if (frame.liveCapabilities) {
        const enabled = frame.liveCapabilities.incomingReadReceipts === true;
        return { ...next, enabled, receipts: enabled ? next.receipts : new Map() };
      }
      if (!next.enabled || !sourceMessageId(frame.messageId) || !validReadIdentity(frame)) return next;
      const receipts = new Map(next.receipts);
      receipts.set(frame.messageId!, { sequence: frame.readSequence!, at: frame.readOn! });
      return { ...next, receipts };
    });
  }, []);
  const readOn = (message: LiveReceiptIdentity): string | undefined => {
    if (state.scope !== scope || !state.enabled || !sourceMessageId(message.messageId)
      || !Number.isSafeInteger(message.readSequence) || (message.readSequence ?? 0) <= 0) return;
    const receipt = state.receipts.get(message.messageId!);
    if (receipt && receipt.sequence === message.readSequence) return receipt.at;
    return validReadIdentity(message) ? message.readOn! : undefined;
  };
  return { observe, readOn };
}

function validReadIdentity(message: LiveReceiptIdentity): boolean {
  return Number.isSafeInteger(message.readSequence) && (message.readSequence ?? 0) > 0
    && typeof message.readOn === 'string' && Number.isFinite(Date.parse(message.readOn));
}
